import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MessageCircle } from "lucide-react";

import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { openWhatsAppChat } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const DEFAULT_MESSAGES = [
  { from: "You", text: "Voice note (0:15)" },
  { from: "Min", text: "Summary: Quick test. You sent yourself a voice note to see how Min replies in this chat." },
];

function initialsForName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return "Y";
  }
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

export default function SelfChatTest({
  title = "Send yourself a test voice note",
  description = "Open your own WhatsApp chat, send a voice note, and Min will send the result back in that same chat.",
  chatName = "Your WhatsApp chat",
  chatSubtitle = "You chat with yourself here",
  messages = DEFAULT_MESSAGES,
  planSummary = null,
  ctaLabel = "Open your self chat",
  secondaryCtaLabel = "",
  phoneNumber = "",
  onOpenChat,
  onSecondaryAction,
}) {
  const { tx } = useI18n();
  const [openingChat, setOpeningChat] = useState(false);
  const handleOpenChat = onOpenChat || (() => void openWhatsAppChat({
    sourceStep: "self_chat_test",
    phoneNumber,
  }));

  async function onOpenChatClick() {
    if (openingChat) {
      return;
    }
    setOpeningChat(true);
    try {
      await Promise.resolve(handleOpenChat());
    } finally {
      setOpeningChat(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      <nav className="flex items-center justify-center px-5 py-4">
        <Logo size="sm" />
      </nav>

      <div className="mx-auto flex min-h-[calc(100dvh-68px)] w-full max-w-sm flex-col justify-center px-5 pb-24">
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="space-y-2 text-center">
            <h1 className="font-display text-3xl font-bold leading-tight">{tx(title)}</h1>
            <p className="text-sm text-muted-foreground">{tx(description)}</p>
          </div>

          {planSummary?.title && planSummary?.copy ? (
            <div className="rounded-[1.5rem] border border-border bg-card px-4 py-4 shadow-[0_16px_40px_-32px_rgba(15,23,42,0.35)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                {tx(planSummary.title)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{tx(planSummary.copy)}</p>
            </div>
          ) : null}

          <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
            <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {initialsForName(chatName)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">{chatName}</p>
                <p className="truncate text-xs text-muted-foreground">{tx(chatSubtitle)}</p>
              </div>
            </div>

            <div className="space-y-3 bg-[#EAE6DF] px-4 py-5">
              {messages.map((message, index) => {
                const fromYou = message.from === "You";
                return (
                  <motion.div
                    key={`${message.from}-${index}`}
                    className={`flex ${fromYou ? "justify-end" : "justify-start"}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + (index * 0.12) }}
                  >
                    <div
                      className={`max-w-[84%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                        fromYou
                          ? "bg-[#DCF8C6] text-slate-900"
                          : "bg-white text-foreground"
                      }`}
                    >
                      {!fromYou ? (
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
                          Min
                        </span>
                      ) : null}
                      {tx(message.text)}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => void onOpenChatClick()}
              size="lg"
              disabled={openingChat}
              className="h-14 w-full gap-2 rounded-2xl text-base font-semibold shadow-lg shadow-primary/20"
            >
              {openingChat ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />}
              {openingChat ? tx("Opening WhatsApp…") : tx(ctaLabel)}
            </Button>

            {secondaryCtaLabel && onSecondaryAction ? (
              <Button
                onClick={onSecondaryAction}
                variant="outline"
                size="lg"
                disabled={openingChat}
                className="h-14 w-full rounded-2xl border-border/70 text-base font-semibold"
              >
                {tx(secondaryCtaLabel)}
              </Button>
            ) : null}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
